<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useOpportunitiesStore } from 'dashboard/stores/opportunities';

import Button from 'dashboard/components-next/button/Button.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import OpportunitiesKanbanBoard from 'dashboard/components-next/Opportunities/OpportunitiesKanbanBoard.vue';
import OpportunityCreateDialog from 'dashboard/components-next/Opportunities/OpportunityCreateDialog.vue';

const STAGE_KEYS = [
  'prospecting',
  'qualified',
  'proposal',
  'negotiation',
  'won',
  'lost',
];

const opportunitiesStore = useOpportunitiesStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const view = ref('kanban');
const createDialogRef = ref(null);
const pageNumber = computed(() => Number(route.query?.page) || 1);

const boardRecords = computed(() => opportunitiesStore.getBoardRecords);
const listRecords = computed(() => opportunitiesStore.getOpportunitiesList);
const meta = computed(() => opportunitiesStore.getMeta);
const uiFlags = computed(() => opportunitiesStore.getUIFlags);
const isFetching = computed(
  () => uiFlags.value.fetchingBoard || uiFlags.value.fetchingList
);
const isCreating = computed(() => uiFlags.value.creatingItem);

const stages = computed(() =>
  STAGE_KEYS.map(key => ({
    key,
    label: t(`OPPORTUNITIES.STAGES.${key.toUpperCase()}`),
  }))
);

const opportunitiesByStage = computed(() => {
  const grouped = Object.fromEntries(STAGE_KEYS.map(key => [key, []]));
  boardRecords.value.forEach(opportunity => {
    (grouped[opportunity.stage] ||= []).push(opportunity);
  });
  return grouped;
});

const fetchBoard = () => opportunitiesStore.getBoard();
const fetchList = page =>
  opportunitiesStore.get({ page: page ?? pageNumber.value });

const switchView = newView => {
  view.value = newView;
  if (newView === 'kanban') fetchBoard();
  else fetchList();
};

const showOpportunity = opportunityId => {
  router.push({
    name: 'opportunities_dashboard_show',
    params: { accountId: route.params.accountId, opportunityId },
  });
};

const onMove = async ({ id, stage }) => {
  try {
    await opportunitiesStore.moveStage(id, stage);
  } catch {
    useAlert(t('OPPORTUNITIES.MOVE_STAGE.ERROR'));
    fetchBoard();
  }
};

const openCreateDialog = () => {
  createDialogRef.value?.open();
};

const createOpportunity = async opportunityAttrs => {
  try {
    await opportunitiesStore.create(opportunityAttrs);
    createDialogRef.value?.onSuccess();
    useAlert(t('OPPORTUNITIES.CREATE.MESSAGES.SUCCESS'));
    if (view.value === 'list') fetchList();
  } catch {
    useAlert(t('OPPORTUNITIES.CREATE.MESSAGES.ERROR'));
  }
};

const onPageChange = page => {
  router.replace({ query: { ...route.query, page: page.toString() } });
  fetchList(page);
};

onMounted(() => {
  fetchBoard();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
    >
      <span class="text-lg font-semibold text-n-slate-12">
        {{ t('OPPORTUNITIES.HEADER') }}
      </span>
      <div class="flex items-center gap-2">
        <div class="flex gap-1 p-1 rounded-lg bg-n-alpha-1">
          <button
            class="px-3 py-1 text-sm rounded-md"
            :class="
              view === 'kanban'
                ? 'bg-n-solid-2 text-n-slate-12'
                : 'text-n-slate-11'
            "
            @click="switchView('kanban')"
          >
            {{ t('OPPORTUNITIES.VIEW.KANBAN') }}
          </button>
          <button
            class="px-3 py-1 text-sm rounded-md"
            :class="
              view === 'list'
                ? 'bg-n-solid-2 text-n-slate-12'
                : 'text-n-slate-11'
            "
            @click="switchView('list')"
          >
            {{ t('OPPORTUNITIES.VIEW.LIST') }}
          </button>
        </div>
        <Button
          :label="t('OPPORTUNITIES.ACTIONS.CREATE')"
          @click="openCreateDialog"
        />
      </div>
    </header>

    <div v-if="isFetching" class="flex items-center justify-center flex-1">
      <span class="text-sm text-n-slate-11">{{
        t('OPPORTUNITIES.LOADING')
      }}</span>
    </div>

    <OpportunitiesKanbanBoard
      v-else-if="view === 'kanban'"
      class="flex-1 overflow-hidden"
      :stages="stages"
      :opportunities-by-stage="opportunitiesByStage"
      @move="onMove"
      @show="showOpportunity"
    />

    <div v-else class="flex flex-col flex-1 px-6 py-4 overflow-y-auto">
      <div
        v-if="listRecords.length === 0"
        class="flex items-center justify-center p-8"
      >
        <span class="text-sm text-n-slate-11">{{
          t('OPPORTUNITIES.EMPTY_STATE.TITLE')
        }}</span>
      </div>
      <table v-else class="w-full text-sm text-left">
        <thead>
          <tr class="text-n-slate-11 border-b border-n-weak">
            <th class="py-2">{{ t('OPPORTUNITIES.FIELDS.NAME') }}</th>
            <th class="py-2">{{ t('OPPORTUNITIES.FIELDS.STAGE') }}</th>
            <th class="py-2">{{ t('OPPORTUNITIES.FIELDS.VALUE') }}</th>
            <th class="py-2">
              {{ t('OPPORTUNITIES.FIELDS.EXPECTED_CLOSE_DATE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="opportunity in listRecords"
            :key="opportunity.id"
            class="border-b cursor-pointer border-n-weak hover:bg-n-alpha-1"
            @click="showOpportunity(opportunity.id)"
          >
            <td class="py-2 text-n-slate-12">{{ opportunity.name }}</td>
            <td class="py-2">
              {{ t(`OPPORTUNITIES.STAGES.${opportunity.stage.toUpperCase()}`) }}
            </td>
            <td class="py-2">
              {{ opportunity.currency }} {{ opportunity.value }}
            </td>
            <td class="py-2">{{ opportunity.expectedCloseDate }}</td>
          </tr>
        </tbody>
      </table>
      <PaginationFooter
        v-if="listRecords.length"
        current-page-info="OPPORTUNITIES_LAYOUT.PAGINATION_FOOTER.SHOWING"
        :current-page="pageNumber"
        :total-items="Number(meta.totalCount || 0)"
        :items-per-page="25"
        class="mt-4"
        @update:current-page="onPageChange"
      />
    </div>

    <OpportunityCreateDialog
      ref="createDialogRef"
      :is-loading="isCreating"
      @create="createOpportunity"
    />
  </div>
</template>
